import * as React from "react";
import { useState, useEffect } from "react";
import { 
  ArrowLeft,
  LogOut,
  User,
  Settings,
  Box,
  Book,
  FileText,
  Lightbulb,
  BarChart3,
  CreditCard,
  Plug,
  Lock
} from "lucide-react";
import AIUsagePanel from "../../features/chat/components/organisms/AIUsagePanel";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  user?: {
    email?: string;
    name?: string;
  };
}

type TabId = 'profile' | 'global_settings' | 'schemas' | 'blocks' | 'knowledge_bases' | 'templates' | 'prompt_library' | 'usage_limits' | 'subscription' | 'integrations';

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  onLogout,
  user
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  
  // Responsiveness state
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Threshold to switch to icon mode (collapsed sidebar)
  // If width is less than 700px, sidebar collapses to make room for content
  const isCollapsed = windowWidth < 700;
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (id: TabId) => {
    setActiveTab(id);
  };

  if (!isOpen) return null;

  const sidebarSections = [
    {
      title: "GENERAL",
      items: [
        // { id: 'global_settings', label: "Global Settings", icon: Settings24Regular, disabled: true },
        { id: 'profile', label: "Profile", icon: User, disabled: false }
      ]
    },
    {
      title: "STUDIO RESOURCES",
      items: [
        // { id: 'schemas', label: "Schemas", icon: DocumentCopy24Regular, disabled: true },
        { id: 'knowledge_bases', label: "Knowledge Bases", icon: Book, disabled: true },
        { id: 'templates', label: "Templates", icon: FileText, disabled: true },
        { id: 'blocks', label: "Blocks", icon: Box, disabled: true },
        { id: 'prompt_library', label: "Prompt Library", icon: Lightbulb, disabled: true }
      ]
    },
    {
      title: "BILLING",
      items: [
        { id: 'usage_limits', label: "Usage & Limits", icon: BarChart3, disabled: false },
        // { id: 'subscription', label: "Subscription", icon: Payment24Regular, disabled: true }
      ]
    },
    // {
    //   title: "CONNECTIONS",
    //   items: [
    //     { id: 'integrations', label: "Integrations", icon: PlugConnected24Regular, disabled: true }
    //   ]
    // }
  ];

  const renderContent = () => {
    if (activeTab === 'profile') {
      return (
        <div className="p-6 w-full max-w-full font-sans">
          <h2 className="text-xl font-semibold mb-2 text-gray-900">Profile</h2>
          <p className="text-gray-500 mb-6 text-sm">Manage your personal information and account settings.</p>
          
          <Card>
            <CardHeader className="border-b py-4">
              <CardTitle className="text-sm font-semibold m-0 text-gray-900">Personal Information</CardTitle>
            </CardHeader>
            
            <CardContent className="p-4 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-900/10 flex items-center justify-center text-gray-900 shrink-0">
                  <User className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm mb-0.5 text-gray-900">{user?.name || 'User'}</div>
                  <div className="text-gray-500 text-xs truncate">{user?.email || 'user@example.com'}</div>
                </div>
              </div>

              <div className={cn("grid gap-4", isCollapsed ? "grid-cols-1" : "grid-cols-2")}>
                <div className="space-y-2">
                  <Label className="text-xs">Full Name</Label>
                  <Input 
                    value={user?.name || ''} 
                    readOnly 
                    className="bg-gray-50 text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Email Address</Label>
                  <Input 
                    value={user?.email || ''} 
                    readOnly 
                    className="bg-gray-50 text-xs"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (activeTab === 'usage_limits') {
      return <AIUsagePanel isCollapsed={isCollapsed} />;
    }

    // Placeholder for other tabs
    const activeItem = sidebarSections.flatMap(s => s.items).find(i => i.id === activeTab);
    
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-gray-400 font-sans">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3 text-gray-500">
          <Lock className="w-6 h-6" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">
          {activeItem?.label || 'Feature'} Locked
        </h3>
        <p className="text-center max-w-[300px] text-gray-500 text-xs">
          This feature is not available in your current plan or is under development.
        </p>
      </div>
    );
  };

  const renderSidebar = () => (
    <div 
      className={cn(
        "bg-gray-900 border-r border-gray-900 flex flex-col shrink-0 h-full transition-all",
        isCollapsed ? "w-[60px]" : "w-[200px]"
      )}
    >
      {/* Sidebar Header */}
      <div className={cn(
        "p-3 border-b border-white/10 flex items-center gap-2",
        isCollapsed ? "justify-center" : "justify-start"
      )}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/20 h-8 w-8"
          title="Close"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        {!isCollapsed && (
          <span className="font-semibold text-white text-xs whitespace-nowrap overflow-hidden">Settings</span>
        )}
      </div>

      {/* Sidebar Menu */}
      <div className="flex-1 overflow-y-auto py-3">
        {sidebarSections.map((section, index) => (
          <div key={index} className="mb-6">
            {!isCollapsed && (
              <div className="px-3 text-[10px] font-semibold text-white/60 tracking-wider mb-1 uppercase whitespace-nowrap overflow-hidden">
                {section.title}
              </div>
            )}
            {isCollapsed && index > 0 && (
               <Separator className="bg-white/10 mx-3 my-2 w-auto" />
            )}
            <div className="flex flex-col gap-0.5 px-2">
              {section.items.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    onClick={() => handleTabChange(item.id as TabId)}
                    disabled={item.disabled}
                    title={isCollapsed ? item.label : undefined}
                    className={cn(
                      "w-full justify-start h-8 text-xs font-normal",
                      isCollapsed ? "px-0 justify-center" : "px-3",
                      isActive 
                        ? "bg-white/15 text-white font-medium hover:bg-white/20 hover:text-white" 
                        : "text-white/80 hover:bg-white/10 hover:text-white",
                      item.disabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <item.icon className={cn("w-5 h-5", !isCollapsed && "mr-2")} />
                    {!isCollapsed && <span>{item.label}</span>}
                  </Button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Sidebar Footer - Logout */}
      <div className="p-3 border-t border-white/10 flex justify-center">
        <Button
          variant="ghost"
          onClick={onLogout}
          title={isCollapsed ? "Sign out" : undefined}
          className={cn(
            "text-red-300 hover:text-red-200 hover:bg-red-900/20 w-full justify-start h-9",
            isCollapsed && "justify-center px-0"
          )}
        >
          <LogOut className={cn("w-5 h-5", !isCollapsed && "mr-2")} />
          {!isCollapsed && <span>Sign out</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-50 z-[2000] flex flex-row animate-in slide-in-from-right-full duration-200 font-sans">
      {renderSidebar()}
      
      <div className="flex-1 overflow-y-auto flex flex-col h-full w-full min-w-0">
        {renderContent()}
      </div>
    </div>
  );
};

export default SettingsPanel;
